import React, { useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { X, Download, Share2, Ticket } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import { MOODS } from "@/lib/moods";
import { toast } from "sonner";

const monthLabel = () => {
  const d = new Date();
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" }).toUpperCase();
};

export default function VoixPassport({ onClose }) {
  const { stats, hours, moodStats, favorites } = usePlayer();
  const { t, lang } = useI18n();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const topCountries = useMemo(() => {
    if (!stats?.counts) return [];
    return Object.entries(stats.counts).sort(([, a], [, b]) => b - a).slice(0, 3);
  }, [stats]);

  const distinctCountries = useMemo(
    () => Object.keys(stats?.counts || {}).length,
    [stats]
  );

  const favMood = useMemo(() => {
    if (!moodStats?.counts) return null;
    const top = Object.entries(moodStats.counts).sort(([, a], [, b]) => b - a)[0];
    if (!top) return null;
    return MOODS.find((m) => m.key === top[0]) || { key: top[0], color: "cyan" };
  }, [moodStats]);

  const totalPlays = useMemo(
    () => Object.values(stats?.counts || {}).reduce((a, b) => a + b, 0),
    [stats]
  );
  const hoursListened = useMemo(() => {
    const s = hours?.seconds || 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return { h, m, total: s };
  }, [hours]);

  const passportId = useMemo(() => {
    const raw = `voix-${stats?.month || "----"}-${totalPlays}`;
    let hash = 0;
    for (let i = 0; i < raw.length; i++) hash = (hash * 31 + raw.charCodeAt(i)) | 0;
    return `#${Math.abs(hash).toString(36).toUpperCase().slice(0, 8)}`;
  }, [stats, totalPlays]);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#050505",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `voix-passport-${stats?.month || "current"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t("passport.downloaded"));
    } catch (err) {
      console.warn("[voix] passport export failed:", err);
      toast.error(t("passport.export_failed"));
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    const text = `My Voix Passport — ${hoursListened.h}h${hoursListened.m}m of world radio across ${distinctCountries} countries this month.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Voix Passport", text, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.origin}`);
        toast.success(t("toast.share_copied"));
      }
    } catch (err) {
      if (err && err.name !== "AbortError") console.warn("[voix] passport share failed:", err);
    }
  };

  const empty = totalPlays === 0;

  return (
    <div
      data-testid="passport-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Ticket size={16} className="text-cyan-400" />
            <div>
              <div className="font-heading font-black text-base tracking-tight">{t("passport.title").toUpperCase()}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">{t("passport.subtitle")}</div>
            </div>
          </div>
          <button
            data-testid="passport-close"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white border border-[var(--voix-border)]"
          >
            <X size={14} />
          </button>
        </div>

        {/* The shareable card */}
        <div
          ref={cardRef}
          data-testid="passport-card"
          className="relative bg-[#050505] border border-white/10 aspect-[3/4] overflow-hidden"
          style={{ boxShadow: "0 20px 60px -20px rgba(6, 182, 212, 0.35)" }}
        >
          {/* Ambient gradient */}
          <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-cyan-500/25 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-60 h-60 rounded-full bg-pink-500/20 blur-3xl" />

          <div className="relative h-full flex flex-col p-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-[0.35em] text-cyan-400">VOIX · PASSPORT</div>
                <div className="font-heading font-black text-2xl tracking-tight mt-1">{monthLabel()}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">{t("passport.id")}</div>
                <div className="font-mono-tech text-[10px] text-slate-300 mt-0.5">{passportId}</div>
              </div>
            </div>

            {empty ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <Ticket size={48} className="text-slate-700 mb-3" strokeWidth={1.2} />
                <div className="font-heading font-bold text-lg">{t("passport.empty.title")}</div>
                <div className="font-body text-xs text-slate-500 mt-1 max-w-[240px]">{t("passport.empty.subtitle")}</div>
              </div>
            ) : (
              <>
                {/* Hero number */}
                <div className="mt-8">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">{t("passport.hours_label")}</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-heading font-black text-6xl tracking-tighter text-white">{hoursListened.h}</span>
                    <span className="font-heading font-black text-xl text-cyan-400">h</span>
                    <span className="font-heading font-black text-4xl tracking-tighter text-white ml-2">{String(hoursListened.m).padStart(2, "0")}</span>
                    <span className="font-heading font-black text-xl text-cyan-400">m</span>
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mt-1">{t("passport.hours_of_radio")}</div>
                </div>

                {/* Countries */}
                <div className="mt-6">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-pink-400 mb-2">{t("passport.top_countries")}</div>
                  {topCountries.length === 0 ? (
                    <div className="font-mono-tech text-[10px] text-slate-500">—</div>
                  ) : (
                    <div className="space-y-1.5">
                      {topCountries.map(([country, count], i) => (
                        <div key={country} className="flex items-baseline gap-3">
                          <span className="font-heading font-black text-2xl text-white leading-none">{i + 1}</span>
                          <span className="font-heading font-bold text-sm text-slate-100 truncate flex-1">{country}</span>
                          <span className="font-mono-tech text-[10px] text-slate-500">×{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Fav mood */}
                {favMood && (
                  <div className="mt-6">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">{t("passport.fav_mood")}</div>
                    <div className="font-heading font-black text-2xl tracking-tight">{t(`mood.${favMood.key}`) || favMood.key}</div>
                  </div>
                )}

                {/* Footer stats */}
                <div className="mt-auto pt-6 grid grid-cols-3 gap-2 border-t border-white/10">
                  <MiniStat label={t("passport.plays")} value={totalPlays} />
                  <MiniStat label={t("passport.countries")} value={distinctCountries} />
                  <MiniStat label={t("passport.favorites")} value={favorites.length} />
                </div>
              </>
            )}

            {/* Watermark */}
            <div className="mt-4 flex items-center justify-between">
              <div className="font-heading font-black text-sm tracking-tight">VOIX<span className="text-cyan-400">.</span></div>
              <div className="font-mono-tech text-[8px] uppercase tracking-[0.3em] text-slate-600">WORLD · RADIO · {String(lang).toUpperCase()}</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <button
            data-testid="passport-download"
            onClick={download}
            disabled={empty || downloading}
            className="flex-1 h-11 border border-cyan-500 text-cyan-300 hover:bg-cyan-500/10 font-mono-tech text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={13} />
            <span>{downloading ? t("passport.exporting") : t("passport.download")}</span>
          </button>
          <button
            data-testid="passport-share"
            onClick={share}
            disabled={empty}
            className="flex-1 h-11 border border-pink-500 text-pink-300 hover:bg-pink-500/10 font-mono-tech text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Share2 size={13} />
            <span>{t("passport.share")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="font-heading font-black text-lg tracking-tight text-white">{value}</div>
      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}
